import { Search, RefreshCw, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { type MonitorFilters, DEFAULT_FILTERS } from "./types";
import { useEffect, useState } from "react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

interface Props {
  value: MonitorFilters;
  onChange: (v: MonitorFilters) => void;
  onRefresh: () => void;
  refreshing: boolean;
  paused: boolean;
}

/** 모니터링 필터 + 검색 + 정렬 + 새로고침 컨트롤. */
export function FilterBar({ value, onChange, onRefresh, refreshing, paused }: Props) {
  // 검색은 디바운스 (300ms)
  const [qDraft, setQDraft] = useState(value.q ?? "");
  const debouncedQ = useDebouncedValue(qDraft, 300);

  useEffect(() => {
    if ((value.q ?? "") !== debouncedQ) {
      onChange({ ...value, q: debouncedQ || undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  const set = <K extends keyof MonitorFilters>(k: K, v: MonitorFilters[K]) =>
    onChange({ ...value, [k]: v });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="질문/답변 본문 검색"
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
            className="pl-8"
          />
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={refreshing}
          className="gap-1"
        >
          <RefreshCw className={refreshing ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
          새로고침
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange(DEFAULT_FILTERS)}
          className="gap-1"
        >
          <RotateCcw className="h-4 w-4" />
          필터 초기화
        </Button>

        <label
          className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground"
          title="삭제(숨김) 처리한 이력을 함께 표시합니다. 이 화면에서 복원할 수 있습니다."
        >
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={value.include_deleted}
            onChange={(e) => set("include_deleted", e.target.checked)}
          />
          삭제된 이력 보기
        </label>

        {paused && (
          <span className="ml-auto text-xs text-muted-foreground">
            ⏸︎ 비활성 탭 — 폴링 일시정지
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-6">
        <Field label="시작 (KST)">
          <Input
            type="datetime-local"
            value={value.from?.slice(0, 16) ?? ""}
            onChange={(e) => set("from", e.target.value || undefined)}
          />
        </Field>
        <Field label="종료 (KST)">
          <Input
            type="datetime-local"
            value={value.to?.slice(0, 16) ?? ""}
            onChange={(e) => set("to", e.target.value || undefined)}
          />
        </Field>
        <Field label="사용자 이메일">
          <Input
            placeholder="사용자 이메일"
            value={value.user_id ?? ""}
            onChange={(e) => set("user_id", e.target.value || undefined)}
          />
        </Field>
        <Field label="사용자 이름">
          <Input
            placeholder="사용자 이름"
            value={value.user_name ?? ""}
            onChange={(e) => set("user_name", e.target.value || undefined)}
          />
        </Field>
        <Field label="답변 여부">
          <Select
            value={value.answered}
            onChange={(e) => set("answered", e.target.value as MonitorFilters["answered"])}
          >
            <option value="any">전체</option>
            <option value="true">답변</option>
            <option value="false">미답변</option>
          </Select>
        </Field>
        <Field label="KMS 저장">
          <Select
            value={value.saved}
            onChange={(e) => set("saved", e.target.value as MonitorFilters["saved"])}
          >
            <option value="any">전체</option>
            <option value="true">저장됨</option>
            <option value="false">미저장</option>
          </Select>
        </Field>
        <Field label="응답시간 임계 (ms)">
          <Input
            type="number"
            min={0}
            placeholder="100"
            value={value.slow_threshold_ms ?? ""}
            onChange={(e) =>
              set(
                "slow_threshold_ms",
                e.target.value ? Number(e.target.value) : undefined,
              )
            }
          />
        </Field>
        <Field label="정렬">
          <Select
            value={value.sort}
            onChange={(e) => set("sort", e.target.value as MonitorFilters["sort"])}
          >
            <option value="time_desc">시각 ↓ 최신</option>
            <option value="time_asc">시각 ↑ 오래된</option>
            <option value="response_desc">응답시간 ↓ 느림</option>
            <option value="response_asc">응답시간 ↑ 빠름</option>
            <option value="user_asc">사용자 ↑</option>
          </Select>
        </Field>
        <Field label="채널">
          <Select
            value={value.channel}
            onChange={(e) => set("channel", e.target.value as MonitorFilters["channel"])}
          >
            <option value="any">전체</option>
            <option value="nw">네이버웍스</option>
            <option value="web">웹</option>
          </Select>
        </Field>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
